import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { parseRolesString } from "../utils/helpers";
import { apiClient, AUTH_EXPIRED_EVENT } from "../utils/httpClient";
import { buildApiUrl } from "../utils/urlConfig";

type AuthUser = {
  email?: string;
  name?: string;
  [key: string]: unknown;
} | null;

type UserContextType = {
  authUser: AuthUser;
  setAuthUser: React.Dispatch<React.SetStateAction<AuthUser>>;
  isAuth: boolean;
  setIsAuth: React.Dispatch<React.SetStateAction<boolean>>;
  roles: string[];
  setRoles: React.Dispatch<React.SetStateAction<string[]>>;
  isLoading: boolean;
  checkAuth: () => Promise<void>;
  clearAuth: () => void;
};

export const LOGIN_ERROR_KEY = "loginError";

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [authUser, setAuthUser] = useState<AuthUser>(null);
  const [isAuth, setIsAuth] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const isTokenExpired = useCallback((token: string) => {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) {
        return true;
      }

      const payload = JSON.parse(atob(parts[1]));
      if (typeof payload?.exp !== "number") {
        return true;
      }

      return Date.now() / 1000 >= payload.exp;
    } catch {
      return true;
    }
  }, []);

  const [roles, setRoles] = useState<string[]>(() => {
    const storedRoles = sessionStorage.getItem("roles");
    if (storedRoles) {
      return parseRolesString(storedRoles);
    }
    return [];
  });

  const clearAuth = useCallback(() => {
    setAuthUser(null);
    setIsAuth(false);
    setRoles([]);
    sessionStorage.removeItem("roles");
    sessionStorage.removeItem("token");
  }, []);

  const fetchToken = useCallback(async () => {
    try {
      const response = await apiClient.get(
        buildApiUrl("/token"),
      );

      const data = response.data;

      if (data?.access_token) {
        sessionStorage.setItem("token", JSON.stringify(data.access_token));

        if (typeof data.authorities === "string") {
          const parsedRoles = parseRolesString(data.authorities);
          sessionStorage.setItem("roles", JSON.stringify(parsedRoles));
          setRoles(parsedRoles);
        } else if (Array.isArray(data.authorities)) {
          sessionStorage.setItem("roles", JSON.stringify(data.authorities));
          setRoles(data.authorities);
        } else {
          setRoles([]);
          sessionStorage.removeItem("roles");
        }
      }
    } catch (err) {
      console.error("Token fetch error:", err);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(
        buildApiUrl("/login"),
      );
      setAuthUser(response.data);
      setIsAuth(true);
      await fetchToken();
    } catch (error) {
      // No ongoing employment -> no access; the login page shows why.
      const response = (
        error as { response?: { status?: number; data?: { code?: string } } }
      )?.response;
      if (response?.status === 403 && response.data?.code === "ACCOUNT_INACTIVE") {
        sessionStorage.setItem(LOGIN_ERROR_KEY, "account_inactive");
      }
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  }, [clearAuth, fetchToken]);

  useEffect(() => {
    const storedToken = sessionStorage.getItem("token");

    if (!storedToken) {
      checkAuth();
      return;
    }

    try {
      const parsedToken = JSON.parse(storedToken);
      const token = typeof parsedToken === "string" ? parsedToken : "";

      if (!token || isTokenExpired(token)) {
        clearAuth();
      }
    } catch {
      clearAuth();
    }
    checkAuth();
  }, [checkAuth, clearAuth, isTokenExpired]);

  useEffect(() => {
    const onFocus = () => {
      // checkAuth();
    };

    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("focus", onFocus);
    };
  }, [checkAuth]);

  useEffect(() => {
    const onAuthExpired = () => {
      clearAuth();
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);

    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
    };
  }, [clearAuth]);

  useEffect(() => {
    if (roles.length === 0) {
      sessionStorage.removeItem("roles");
      return;
    }

    sessionStorage.setItem("roles", JSON.stringify(roles));
  }, [roles]);

  return (
    <UserContext.Provider
      value={{
        authUser,
        setAuthUser,
        isAuth,
        setIsAuth,
        roles,
        setRoles,
        isLoading,
        checkAuth,
        clearAuth,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be used within a UserProvider");
  return context;
};
